<template>
  <div
    v-if="show"
    class="fixed z-10 inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center text-base"
  >
    <div
      class="bg-white rounded-lg shadow-xl w-10/12 max-h-[90vh] flex flex-col"
    >
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b shrink-0">
        <h2 class="text-2xl text-gray-800 font-bold">Register Patient</h2>
        <button
          type="button"
          class="ml-auto p-1.5 text-gray-500 hover:text-custom-yellow"
          @click="closePatientRegistrationModal"
          aria-label="Close"
        >
          <svg
            class="w-3 h-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 14 14"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
        </button>
      </div>

      <!-- Scrollable Content Area -->
      <div class="flex-grow overflow-y-auto p-4">
        <!-- Error Section -->
        <div
          v-if="errors.length"
          class="p-4 mb-4 border border-red-500 rounded-lg bg-red-50"
        >
          <h2 class="text-lg font-bold text-red-700 mb-2">Validation Errors</h2>
          <ul class="list-disc pl-5 text-red-600">
            <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
          </ul>
        </div>

        <!-- Tabs -->
        <ul
          class="flex flex-wrap text-sm font-medium text-center text-gray-500 border-b border-gray-200"
          role="tablist"
        >
          <li class="mr-2">
            <button
              :class="tab === 'tab1' ? activeTabClass : inactiveTabClass"
              @click="tab = 'tab1'"
              role="tab"
            >
              Personal Information
            </button>
          </li>
          <li class="mr-2">
            <button
              :class="tab === 'tab2' ? activeTabClass : inactiveTabClass"
              @click="tab = 'tab2'"
              role="tab"
            >
              Emergency Contact
            </button>
          </li>
          <!-- Show Payment Info tab only when visit type is not Registration Only -->
          <li class="mr-2" v-if="patientRegForm.patient.visitType === 'Consultation' || patientRegForm.patient.visitType === 'Procedure/Services'">
            <button
              :class="tab === 'tab3' ? activeTabClass : inactiveTabClass"
              @click="tab = 'tab3'"
              role="tab"
            >
              Payment Info
            </button>
          </li>
          <li class="mr-2">
            <button
              :class="tab === 'tab4' ? activeTabClass : inactiveTabClass"
              @click="tab = 'tab4'"
              role="tab"
            >
              Document Upload
            </button>
          </li>
          <!-- Consultation Type Tab - appears before Declaration -->
          <li class="mr-2" v-if="patientRegForm.patient.visitType === 'Consultation'">
            <button
              :class="tab === 'tab5' ? activeTabClass : inactiveTabClass"
              @click="tab = 'tab5'"
              role="tab"
            >
              Consultation Type
            </button>
          </li>
          <!-- Declaration Tab -->
          <li class="mr-2">
            <button
              :class="tab === 'tab6' ? activeTabClass : inactiveTabClass"
              @click="tab = 'tab6'"
              role="tab"
            >
              Declaration
            </button>
          </li>
          <!-- Procedure/Services Tab -->
          <li class="mr-2" v-if="patientRegForm.patient.visitType === 'Procedure/Services'">
            <button
              :class="tab === 'tab7' ? activeTabClass : inactiveTabClass"
              @click="tab = 'tab7'"
              role="tab"
            >
              Procedures/Services
            </button>
          </li>
        </ul>

        <!-- Tab Content -->
        <div class="mt-4">
          <form @submit.prevent>
            <div v-if="tab === 'tab1'" class="p-4 border rounded-lg">
              <PatientInformationForm
                ref="pstientInfoModal"
                v-model:formData="patientRegForm"
                v-model:relationships="relationships"
                :isSearchingPatient="isSearchingPatient"
                @searchPatient="lookupPatient"
              />
            </div>
            <div v-if="tab === 'tab2'" class="p-4 border rounded-lg">
              <PatientEmergencyContactForm
                ref="patientEmergencyContact"
                v-model:formData="patientRegForm"
                v-model:relationships="relationships"
                :existingEmergencyContacts="emergencyContacts"
              />
            </div>
            <div
              v-if="tab === 'tab3'"
              class="space-y-6 flex flex-col justify-center items-center mt-6"
            >
              <PatientSchemeForm v-model:formData="patientRegForm" />
            </div>
            <div v-if="tab === 'tab4'" class="space-y-6">
              <PatientDocumentUploadForm
                v-model:formData="patientRegForm"
                @uploading="handleFileLoading"
                :existingUploadedDocuments="existingUploadedDocuments"
              />
            </div>
            <!-- Consultation Type Tab Content -->
            <div v-if="tab === 'tab5' && patientRegForm.patient.visitType === 'Consultation'" class="space-y-6">
              <PatientConsultationService v-model:formData="patientRegForm" />
            </div>
            <!-- Declaration Tab Content -->
            <div v-if="tab === 'tab6'" class="space-y-6">
              <PatientDeclarationForm v-model:formData="patientRegForm" />
            </div>
            <!-- Procedure/Services Tab Content -->
            <div v-if="tab === 'tab7' && patientRegForm.patient.visitType === 'Procedure/Services'" class="space-y-6">
              <PatientServices
                :ref="comp => { if (comp && tab === 'tab7') patientServicesInstance = comp; }"
                v-model:formData="patientRegForm"
              />
            </div>
          </form>
        </div>
      </div>

      <!-- Footer -->
      <div class="p-4 border-t shrink-0 flex justify-end space-x-4">
        <button
          type="button"
          @click="closePatientRegistrationModal"
          class="px-6 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Cancel
        </button>
        <button
          type="button"
          @click="toggleNextTab"
          :disabled="localShowLoader || isLoading || hasSubmitted"
          class="relative px-6 py-2 bg-custom-yellow text-white hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500"
        >
          <span v-if="!isLoading">
            {{ isLastTab ? (localShowLoader ? "Submitting..." : "Submit") : "Next" }}
          </span>
          <span v-else class="flex justify-center items-center">
            <svg
              class="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
                fill="none"
              />
            </svg>
          </span>
        </button>
      </div>
    </div>
  </div>

  <!-- Patient Selection Popup -->
  <div
    v-if="showPatientPopup"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
  >
    <div
      class="bg-white p-6 shadow-xl w-9/12 rounded-2xl flex flex-col min-h-[300px] max-h-[80vh]"
    >
      <h3 class="text-lg font-bold mb-4">Select Patient</h3>
      <div class="flex-grow overflow-y-auto">
        <div class="space-y-2">
          <template v-if="matchingPatients.length > 0">
            <div
              v-for="patient in matchingPatients"
              :key="patient.id"
              class="grid grid-cols-12 items-start py-3 bg-gray-50"
            >
              <div class="col-span-2">
                <div class="text-sm text-gray-600">Name:</div>
                <div class="font-medium">{{ patient.FullName }}</div>
              </div>
              <div class="col-span-2">
                <div class="text-sm text-gray-600">ID Number:</div>
                <div class="font-medium">{{ patient.IdNumber }}</div>
              </div>
              <div class="col-span-2">
                <div class="text-sm text-gray-600">Phone Number:</div>
                <div class="font-medium">{{ patient.PhoneNumber }}</div>
              </div>
              <div class="col-span-2">
                <div class="text-sm text-gray-600">DOB:</div>
                <div class="font-medium">{{ patient.Dob }}</div>
              </div>
              <div class="col-span-2">
                <div class="text-sm text-gray-600">Relationship:</div>
                <div class="font-medium">{{ patient.Relationship }}</div>
              </div>
              <div class="col-span-2 text-right">
                <button
                  @click="selectPatient(patient)"
                  class="px-4 py-2 bg-custom-blue text-white rounded hover:bg-blue-800 transition-colors text-sm"
                >
                  Select
                </button>
              </div>
            </div>
          </template>
          <div v-else class="text-center py-4 text-gray-500">
            No matching patients found.
          </div>
        </div>
      </div>
      <div class="mt-6 flex justify-end">
        <button
          @click="showPatientPopup = false"
          class="px-6 py-2 bg-gray-100 text-gray-800 rounded hover:bg-gray-200"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { initFlowbite } from "flowbite";
import { ref, nextTick } from "vue";
import { useToast } from "vue-toast-notification";
import PatientInformationForm from "@/components/forms/patient/registration/PatientInformationForm.vue";
import PatientEmergencyContactForm from "@/components/forms/patient/registration/PatientEmergencyContactForm.vue";
import PatientDocumentUploadForm from "@/components/forms/patient/registration/PatientDocumentUploadForm.vue";
import PatientDeclarationForm from "@/components/forms/patient/registration/PatientDeclarationForm.vue";
import PatientSchemeForm from "@/components/forms/patient/registration/PatientSchemeForm.vue";
import PatientServices from "@/components/forms/patient/registration/PatientServices.vue";
import PatientConsultationService from "@/components/forms/patient/registration/PatientConsultationService.vue";
import apiClient from "@/services/api";
import { debounce } from "lodash";

export default {
  name: "PatientRegModal",
  components: {
    PatientInformationForm,
    PatientEmergencyContactForm,
    PatientDocumentUploadForm,
    PatientDeclarationForm,
    PatientSchemeForm,
    PatientServices,
    PatientConsultationService,
  },
  setup() {
    const toast = useToast();
    const patientServicesInstance = ref(null);

    const getProcedureServicesPayload = () => {
      return patientServicesInstance.value?.submitRequests?.() || {};
    };

    return {
      toast,
      patientServicesInstance,
      getProcedureServicesPayload,
    };
  },
  props: {
    formData: {
      type: Object,
      required: true,
      default: () => ({
        patient: {
          schemes: [],
          emergencyContact: {},
          agreements: {
            acknowledgementConsent: true,
            consentFinancial: true,
            consentForTreatment: true,
          },
          patientInsuranceInfo: {},
          uploadedDocuments: [],
          lab: [],
          prescription: [],
        },
      }),
    },
    show: {
      type: Boolean,
      required: true,
    },
    showLoader: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      patientRegForm: {
        ...this.formData,
        patient: {
          ...this.formData.patient,
          schemes: this.formData.patient.schemes || [],
          emergencyContact: this.formData.patient.emergencyContact || {},
          agreements: {
            acknowledgementConsent: true,
            consentFinancial: true,
            consentForTreatment: true,
          },
          patientInsuranceInfo: this.formData.patient.patientInsuranceInfo || {},
          uploadedDocuments: this.formData.patient.uploadedDocuments || [],
          lab: this.formData.patient.lab || [],
          prescription: this.formData.patient.prescription || [],
          consentChecked: true,
        },
      },
      tab: "tab1",
      countries: [{ id: 1, value: "Kenya", label: "Kenya" }],
      relationships: ["Principal Owner", "Dependent"],
      isLoading: false,
      isSearchingPatient: false,
      localShowLoader: this.showLoader,
      showPatientPopup: false,
      matchingPatients: [],
      emergencyContacts: [],
      existingUploadedDocuments: [],
      fileUploading: false,
      errors: [],
      requests: [],
      hasSubmitted: false,
    };
  },
  computed: {
    activeTabClass() {
      return "inline-block px-4 py-2 font-semibold border-b-2 border-custom-yellow text-custom-blue active";
    },
    inactiveTabClass() {
      return "inline-block px-4 py-2 font-semibold text-gray-500";
    },
    isLastTab() {
      if (this.patientRegForm.patient.visitType === "Consultation") {
        return this.tab === "tab6"; // Declaration is last
      }
      if (this.patientRegForm.patient.visitType === "Procedure/Services") {
        return this.tab === "tab7"; // Procedures/Services is last
      }
      return this.tab === "tab6"; // Default: Declaration
    },
    loader() {
      return this.showLoader || this.fileUploading;
    },
  },
  mounted() {
    initFlowbite();
    this.resetForm();
  },
  watch: {
    showLoader(newValue) {
      this.localShowLoader = newValue;
    },
    show(newValue) {
      if (newValue) {
        this.resetForm();
      }
    },
  },
  methods: {
    resetForm() {
      this.patientRegForm = {
        ...this.formData,
        patient: {
          ...this.formData.patient,
          schemes: [],
          emergencyContact: {},
          agreements: {
            acknowledgementConsent: true,
            consentFinancial: true,
            consentForTreatment: true,
          },
          patientInsuranceInfo: {},
          uploadedDocuments: [],
          lab: [],
          prescription: [],
          fullName: "",
          idNumber: "",
          dob: "",
          phoneNumber: "",
          gender: "",
          visitType: "",
          relationship: "",
          selectedMethod: "",
          consentChecked: true,
        },
      };
      this.tab = "tab1";
      this.errors = [];
      this.emergencyContacts = [];
      this.existingUploadedDocuments = [];
      this.requests = [];
      this.showPatientPopup = false;
      this.matchingPatients = [];
      this.hasSubmitted = false;
    },
    async lookupPatient() {
      // Build search payload with available fields
      const payload = {};

      if (this.patientRegForm.patient.idNumber) {
        payload.idNumber = this.patientRegForm.patient.idNumber;
      }

      if (this.patientRegForm.patient.phoneNumber) {
        payload.phoneNumber = this.patientRegForm.patient.phoneNumber;
      }

      if (this.patientRegForm.patient.fullName) {
        payload.FullName = this.patientRegForm.patient.fullName;
      }

      if (this.patientRegForm.patient.dob) {
        payload.dob = this.patientRegForm.patient.dob;
      }

      // Only search if we have at least one search criterion
      if (Object.keys(payload).length === 0) {
        this.showPatientPopup = false;
        return;
      }

      this.isSearchingPatient = true;
      try {
        const response = await apiClient.searchPatientWithDetails(payload);
        this.matchingPatients = response.data;
        this.showPatientPopup = this.matchingPatients.length > 0;
      } catch (error) {
        this.toast.error("Error looking up patient: " + error.message);
      } finally {
        this.isSearchingPatient = false;
      }
    },
    closePatientRegistrationModal() {
      this.resetForm();
      this.hasSubmitted = false;
      this.isLoading = false;
      this.$emit("close");
    },
    toggleNextTab() {
      const tabs = ["tab1", "tab2"];

      // Add Payment Info tab only when visit type is NOT "Registration Only"
      if (this.patientRegForm.patient.visitType !== "Registration Only") {
        tabs.push("tab3");
      }

      // Add Document Upload (always present)
      tabs.push("tab4");

      // Add visit type specific tabs
      if (this.patientRegForm.patient.visitType === "Consultation") {
        tabs.push("tab5", "tab6"); // Consultation Type → Declaration
      } else if (this.patientRegForm.patient.visitType === "Procedure/Services") {
        tabs.push("tab6", "tab7"); // Declaration → Procedures/Services
      } else {
        // Registration Only
        tabs.push("tab6"); // Only Declaration
      }

      const currentTabIndex = tabs.indexOf(this.tab);

      if (this.validateCurrentTab()) {
        this.errors = [];
        if (currentTabIndex < tabs.length - 1) {
          this.tab = tabs[currentTabIndex + 1];
        } else {
          this.debouncedSubmitForm();
        }
      } else {
        this.scrollToTop();
      }
    },
    validateCurrentTab() {
      this.errors = [];
      if (this.tab === "tab1") return this.validPatientInfo();
      if (this.tab === "tab2") return this.validEmergencyContacts();
      if (this.tab === "tab3") return this.validPatientSchemes();
      if (this.tab === "tab4") return this.validDocuments();
      if (this.tab === "tab5" && this.patientRegForm.patient.visitType === "Consultation") {
        if (!this.patientRegForm.patient.consultation_service?.id) {
          this.errors.push("Please select a consultation type.");
          return false;
        }
        return true;
      }
      if (this.tab === "tab6") return this.validateDeclaration();
      if (this.tab === "tab7") return this.validProcedureServices();
      return true;
    },
    validPatientInfo() {
      let isValid = true;

      // MANDATORY: Full Name
      if (!this.patientRegForm.patient.fullName) {
        this.errors.push("Full name is required.");
        isValid = false;
      }

      // MANDATORY: D.O.B
      if (!this.patientRegForm.patient.dob) {
        this.errors.push("Date of Birth is required.");
        isValid = false;
      }

      // MANDATORY: Age
      if (!this.patientRegForm.patient.age) {
        this.errors.push("Age is required.");
        isValid = false;
      }

      // MANDATORY: Gender
      if (!this.patientRegForm.patient.gender) {
        this.errors.push("Gender is required.");
        isValid = false;
      }

      // MANDATORY: Visit Type
      if (!this.patientRegForm.patient.visitType) {
        this.errors.push("Visit type is required.");
        isValid = false;
      }

      // MANDATORY: Relationship
      if (!this.patientRegForm.patient.relationship) {
        this.errors.push("Relationship is required.");
        isValid = false;
      }

      // OPTIONAL: Phone Number - only validate format if provided
      if (this.patientRegForm.patient.phoneNumber && this.$refs.pstientInfoModal?.phoneError) {
        this.errors.push(this.$refs.pstientInfoModal.phoneError);
        isValid = false;
      }

      // OPTIONAL: ID Number - only validate format if provided
      if (this.patientRegForm.patient.idNumber && this.$refs.pstientInfoModal?.idError) {
        this.errors.push(this.$refs.pstientInfoModal.idError);
        isValid = false;
      }

      return isValid;
    },
    validEmergencyContacts() {
      // Emergency contact is now completely optional
      // Only validate phone format if phone number is provided
      let isValid = true;

      if (this.patientRegForm.patient.emergencyContact.phoneNumber &&
          this.$refs.patientEmergencyContact?.phoneError) {
        this.errors.push(this.$refs.patientEmergencyContact.phoneError);
        isValid = false;
      }

      return isValid;
    },
    validPatientSchemes() {

      // Don't require payment scheme for "Registration Only"
      if (this.patientRegForm.patient.visitType === "Registration Only") {
        return true; // Skip validation
      }

      let isValid = true;
      if (!this.patientRegForm.patient.schemes || this.patientRegForm.patient.schemes.length === 0) {
        this.errors.push("At least one Payment Method must be selected.");
        isValid = false;
      }
      return isValid;
    },
    validDocuments() {
      return true;
    },
    validProcedureServices() {
      if (this.patientRegForm.patient.visitType === "Procedure/Services") {
        const requests = this.getProcedureServicesPayload();
        if (!requests || Object.keys(requests).length === 0) {
          this.errors.push("At least one procedure or service is required.");
          return false;
        }
      }
      return true;
    },
    validateDeclaration() {
      let isValid = true;
      if (!this.patientRegForm.patient.agreements.consentForTreatment) {
        this.errors.push("Consent for treatment is required.");
        isValid = false;
      }
      if (!this.patientRegForm.patient.agreements.consentFinancial) {
        this.errors.push("Consent for financial responsibility is required.");
        isValid = false;
      }
      if (!this.patientRegForm.patient.agreements.acknowledgementConsent) {
        this.errors.push("Acknowledgement of privacy policy is required.");
        isValid = false;
      }
      return isValid;
    },
    debouncedSubmitForm: debounce(async function () {
      console.log("debouncedSubmitForm called");
      if (this.hasSubmitted) {
        console.log("Submission blocked: hasSubmitted is true");
        return;
      }
      this.hasSubmitted = true;
      this.isLoading = true;

      try {
        const tabs = ["tab1", "tab2"];

        // Add Payment Info ONLY if NOT "Registration Only"
        if (this.patientRegForm.patient.visitType !== "Registration Only") {
          tabs.push("tab3");
        }

        // Add Document Upload
        tabs.push("tab4");

        // Add visit-type-specific tabs
        if (this.patientRegForm.patient.visitType === "Consultation") {
          tabs.push("tab5", "tab6"); // Consultation Type → Declaration
        } else if (this.patientRegForm.patient.visitType === "Procedure/Services") {
          tabs.push("tab6", "tab7"); // Declaration → Procedures/Services
        } else {
          // Registration Only
          tabs.push("tab6"); // Just Declaration
        }

        // Validate all tabs
        for (const tab of tabs) {
          this.tab = tab;
          if (!this.validateCurrentTab()) {
            this.toast.error("Please address errors in " + this.getTabName(tab));
            this.scrollToTop();
            this.isLoading = false;
            this.hasSubmitted = false;
            return;
          }
        }

        if (this.patientRegForm.patient.visitType === "Procedure/Services") {
          this.requests = this.getProcedureServicesPayload();
          this.patientRegForm = { ...this.patientRegForm, ...this.requests };
        }

        this.patientRegForm.patient.consentChecked =
          this.patientRegForm.patient.agreements.acknowledgementConsent &&
          this.patientRegForm.patient.agreements.consentFinancial &&
          this.patientRegForm.patient.agreements.consentForTreatment;

        const response = await apiClient.createPatient(this.patientRegForm);
        if (response.success) {
          this.$emit("submit", { formData: this.patientRegForm, response });
          this.closePatientRegistrationModal();
        } else {
          this.errors.push(response.message || "Failed to register patient.");
          this.scrollToTop();
          this.hasSubmitted = false;
        }
      } catch (error) {
        this.errors.push("Error registering patient: " + error.message);
        this.scrollToTop();
        this.hasSubmitted = false;
      } finally {
        this.isLoading = false;
      }
    }, 500),
    getTabName(tab) {
      const tabNames = {
        tab1: "Personal Information",
        tab2: "Emergency Contact",
        tab3: "Payment Info",
        tab4: "Document Upload",
        tab5: "Consultation Type",
        tab6: "Declaration",
        tab7: "Procedures/Services",
      };
      return tabNames[tab] || tab;
    },
    updateForm(newFormData) {
      this.patientRegForm = {
        ...newFormData,
        patient: {
          ...newFormData.patient,
          schemes: Array.isArray(newFormData.patient.schemes) ? newFormData.patient.schemes : [],
          agreements: {
            acknowledgementConsent: newFormData.patient.agreements?.acknowledgementConsent ?? true,
            consentFinancial: newFormData.patient.agreements?.consentFinancial ?? true,
            consentForTreatment: newFormData.patient.agreements?.consentForTreatment ?? true,
          },
          consentChecked: (
            (newFormData.patient.agreements?.acknowledgementConsent ?? true) &&
            (newFormData.patient.agreements?.consentFinancial ?? true) &&
            (newFormData.patient.agreements?.consentForTreatment ?? true)
          ),
        },
      };
    },
    handleFileLoading(event) {
      this.fileUploading = event;
    },
    selectPatient(patient) {
      this.patientRegForm.patient.patientId = patient.PatientId;
      Object.keys(this.patientRegForm.patient).forEach((key) => {
        if (patient[this.capitalizeFirstCharacter(key)]) {
          this.patientRegForm.patient[key] = patient[this.capitalizeFirstCharacter(key)];
        }
      });
      if (patient.emergency_contact) {
        this.patientRegForm.patient.emergencyContact = {
          fullName: patient.emergency_contact.Name,
          relationship: patient.emergency_contact.RelationshipToPatient,
          phoneNumber: patient.emergency_contact.PhoneNumber,
        };
        this.emergencyContacts = [{
          fullName: patient.emergency_contact.Name,
          relationship: patient.emergency_contact.RelationshipToPatient,
          phoneNumber: patient.emergency_contact.PhoneNumber,
        }];
      } else {
        this.emergencyContacts = [];
      }
      this.patientRegForm.patient.schemes = Array.isArray(patient.schemes) ? patient.schemes : [];
      this.patientRegForm.patient.selectedMethod = patient.SelectedMethod || "";
      this.patientRegForm.patient.patientInsuranceInfo = {
        insuranceId: patient.patient_insurance_info?.InsuranceId || "",
        memberNumber: patient.patient_insurance_info?.MemberNumber || "",
      };
      this.existingUploadedDocuments = Array.isArray(patient.uploaded_documents) ? patient.uploaded_documents : [];
      this.patientRegForm.patient.agreements = {
        acknowledgementConsent: true,
        consentFinancial: true,
        consentForTreatment: true,
      };
      this.patientRegForm.patient.consentChecked = true;
      this.patientRegForm.lab = [];
      this.patientRegForm.prescription = [];
      this.showPatientPopup = false;
    },
    capitalizeFirstCharacter(str) {
      if (!str || typeof str !== "string") return "";
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    scrollToTop() {
      if (this.$el && typeof this.$el.querySelector === 'function') {
        nextTick(() => {
          const modalContent = this.$el.querySelector(".overflow-y-auto");
          if (modalContent) {
            modalContent.scrollTop = 0;
          }
        });
      }
    },
  },
};
</script>

<style scoped>
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;
}
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}
.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>